import type React from 'react';
import { useRef } from 'react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import styles from './BusinessFlow.module.css';

interface PostmanFilesInfoProps {
  postmanCollection: any;
  postmanEnv: any;
  activeSpec: any;
  uploading: boolean;
  onDownload: (type: string) => Promise<void>;
  onUploadEnvironment: (parsed: any) => Promise<void>;
}

export const PostmanFilesInfo: React.FC<PostmanFilesInfoProps> = ({
  postmanCollection,
  postmanEnv,
  activeSpec,
  uploading,
  onDownload,
  onUploadEnvironment,
}) => {
  const envFileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    envFileInputRef.current?.click();
  };

  const onEnvFileSelected = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await onUploadEnvironment(parsed);
    } catch {
      toast.error('Invalid JSON file');
    }
  };

  return (
    <>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>📦 Postman Files</h2>

        <div className={styles.twoColumnGrid}>
          {postmanCollection && (
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>
                📄 Collection Info
                <span className={styles.statusBadge} data-status="found">
                  Found
                </span>
              </h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Collection Name:</span>
                <span className={styles.infoValue}>{postmanCollection.info?.name || 'N/A'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Schema Version:</span>
                <span className={styles.infoValue}>{postmanCollection.info?.schema?.split('/').pop() || 'N/A'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Total Requests:</span>
                <span className={styles.infoValue}>{postmanCollection.requestCount || 0}</span>
              </div>
              {postmanCollection.info?.description && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Description:</span>
                  <span
                    className={styles.infoValue}
                    style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {postmanCollection.info.description}
                  </span>
                </div>
              )}
              <div className={clsx(styles.buttonGroup, styles.buttonGroupMt)}>
                <button className={styles.button} onClick={() => onDownload('collection')}>
                  💾 Download Collection
                </button>
              </div>
            </div>
          )}

          {postmanEnv && (
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>
                🌍 Environment Info
                <span className={styles.statusBadge} data-status="found">
                  Found
                </span>
              </h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Environment Name:</span>
                <span className={styles.infoValue}>{postmanEnv.name || 'N/A'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Variables Count:</span>
                <span className={styles.infoValue}>{postmanEnv.values?.length || 0}</span>
              </div>
              {postmanEnv._postman_variable_scope && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Scope:</span>
                  <span className={styles.infoValue}>{postmanEnv._postman_variable_scope}</span>
                </div>
              )}
              <div className={clsx(styles.buttonGroup, styles.buttonGroupMt)}>
                <button className={styles.button} onClick={() => onDownload('environment')}>
                  💾 Download Environment
                </button>
                <button className={styles.button} onClick={handleUploadClick} disabled={!activeSpec || uploading}>
                  🔄 Replace Environment
                </button>
              </div>
            </div>
          )}
        </div>

        <input
          type="file"
          accept=".json"
          ref={envFileInputRef}
          onChange={onEnvFileSelected}
          style={{ display: 'none' }}
        />

        {!postmanCollection && (
          <div className={styles.infoCard}>
            <h3 className={styles.infoCardTitle}>
              📄 Collection
              <span className={styles.statusBadge} data-status="not-found">
                Not Found
              </span>
            </h3>
            <p className={styles.infoText}>
              No postman.json file found in the project directory. Generate a new collection to get started.
            </p>
          </div>
        )}

        {!postmanEnv && (
          <div className={clsx(styles.infoCard, styles.infoCardMt)}>
            <h3 className={styles.infoCardTitle}>
              🌍 Environment
              <span className={styles.statusBadge} data-status="not-found">
                Not Found
              </span>
            </h3>
            <p className={styles.infoText}>
              No postman-env.json file found. Upload an environment file to manage different configurations.
            </p>
            <div className={clsx(styles.buttonGroup, styles.buttonGroupMt)}>
              <button className={styles.button} onClick={handleUploadClick} disabled={!activeSpec || uploading}>
                📤 Upload Environment
              </button>
            </div>
          </div>
        )}
      </div>

      {postmanEnv?.values && postmanEnv.values.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>🔧 Environment Variables</h2>
          <div className={styles.envVariablesList}>
            {postmanEnv.values.map((variable: any, index: number) => (
              <div className={styles.envVariable} key={index}>
                <span className={styles.envKey}>{variable.key}</span>
                <span className={styles.envValue}>{variable.value || '(empty)'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
